'use client'

import { JSX } from 'react'

import { InfiniteScroll } from '~/components/InfiniteScroll'
import {
  EmptyState,
  LoadingSpinner,
  Toggleable,
} from '~/components/component-library'
import { useShowDust } from '~/hooks/useShowDust'
import { useIsMobile } from '~/utils/devices'

const MOBILE_ITEM_HEIGHT = 70
const DESKTOP_ITEM_HEIGHT = 80

// Presentational feed shell: callers own data fetching and row rendering, and
// read the same hideDustLocalStorageKey to filter their data.
export function TransactionFeedView<T>({
  title,
  emptyTitle,
  emptyMessage,
  isLoading,
  list,
  hasNextPage,
  loadNextPage,
  renderItem,
  hideDustLocalStorageKey,
}: {
  title: string
  emptyTitle?: string
  emptyMessage: string
  isLoading: boolean
  list: T[]
  hasNextPage: boolean
  loadNextPage: () => Promise<T[] | undefined>
  renderItem: (item: T) => JSX.Element
  hideDustLocalStorageKey: string
}) {
  const isMobile = useIsMobile()
  const [showDust, setShowDust] = useShowDust(hideDustLocalStorageKey)

  return (
    <div className="z-10 flex flex-col md:-ml-3 md:-mr-3 md:space-y-6">
      {isLoading ? (
        <div className="flex h-full w-full items-center justify-center py-4 text-secondary">
          <LoadingSpinner size="xl" />
        </div>
      ) : (
        <>
          <div className="mr-0.5 flex items-center justify-between md:pl-3 md:pr-11">
            <h3>{title}</h3>
            <Toggleable
              layout="compact"
              label="Small transfers"
              tooltip="Toggling this on will display transfers of <$1.00"
              isChecked={showDust}
              onCheckedChange={setShowDust}
            />
          </div>
          {list.length === 0 ? (
            <div className="w-full md:pl-3 md:pr-11">
              <EmptyState title={emptyTitle}>{emptyMessage}</EmptyState>
            </div>
          ) : (
            <InfiniteScroll
              hasNextPage={hasNextPage}
              isNextPageLoading={isLoading}
              list={list}
              loadNextPage={loadNextPage}
              renderItem={renderItem}
              itemHeight={isMobile ? MOBILE_ITEM_HEIGHT : DESKTOP_ITEM_HEIGHT}
            />
          )}
        </>
      )}
    </div>
  )
}
